import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { Lead, User, Note, Task, Activity, InteractionEvent, TimelineEntry } from '../../types';
import { encodeId, decodeId } from '../../utils/hashids';
import { whatsappVendorChatUrl } from '../../utils/whatsappVendor';
import {
  fetchLead, updateLead, deleteLead,
  fetchUsersList, createNote,
  createContact, createOrganization, updateContact, updateOrganization,
  createDeal, updateDeal,
  fetchContact, fetchOrganization,
  fetchLeadTimeline, fetchSimCallAudio,
  deleteNote, updateTask, createTask,
  patchLeadStatus,
} from '../../services/api';
import LeadStatusBadge from './LeadStatusBadge';
import ReachabilityBadge from './ReachabilityBadge';
import RelationshipBadge from './RelationshipBadge';
import IssueChips from './IssueChips';
import LeadStatusEditor from './LeadStatusEditor';
import SummaryHistory from './SummaryHistory';
import type { DealStageValue } from '../../types';

const SimCallAudioPlayer: React.FC<{ simCallId: number }> = ({ simCallId }) => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(false);
  const load = () => {
    if (blobUrl || loading) return;
    setLoading(true);
    fetchSimCallAudio(simCallId)
      .then(url => setBlobUrl(url))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  };
  if (error) return <span style={{ fontSize: 12, color: '#dc2626' }}>Failed to load audio</span>;
  if (!blobUrl) return (
    <button onClick={load} disabled={loading} style={{
      background: loading ? '#f1f5f9' : '#ede9fe', border: 'none', borderRadius: 6,
      padding: '4px 12px', cursor: loading ? 'default' : 'pointer',
      fontSize: 12, fontWeight: 600, color: loading ? '#64748b' : '#7c3aed', marginTop: 4,
    }}>
      {loading ? 'Loading…' : '▶ Play Recording'}
    </button>
  );
  return <audio controls src={blobUrl} style={{ height: 32, width: 200, marginTop: 4 }} />;
};

type Tab = 'overview' | 'qualification' | 'timeline';

const TEMP_COLOR: Record<string, string> = {
  HOT: '#D85A30', WARM: '#EF9F27', COLD: '#64748B',
};

const QUAL_ITEMS = [
  { key: 'budget_confirmed',          label: 'Budget confirmed',          desc: 'Monthly order value stated by lead' },
  { key: 'decision_maker_reached',    label: 'Decision maker reached',    desc: 'Spoke directly with proprietor/owner' },
  { key: 'timeline_stated',           label: 'Timeline stated',           desc: 'Lead has a start date in mind' },
  { key: 'product_interest_confirmed',label: 'Product interest confirmed', desc: 'Specific product range identified' },
];

const initials = (name: string) =>
  name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();

const timeAgo = (iso?: string | null) => {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const fmtInr = (n?: number) =>
  n ? `₹${Number(n).toLocaleString('en-IN')}/mo` : '—';

const InfoRow: React.FC<{ label: string; value?: string | null }> = ({ label, value }) => (
  <div className="ld-info-row">
    <span className="ld-info-label">{label}</span>
    <span className="ld-info-value">{value || <em className="ld-empty">—</em>}</span>
  </div>
);

/* ── Convert Lead Modal ─────────────────────────────────────────────────── */
interface ConvertProps { lead: Lead; onClose: () => void; onConverted: () => void; }

const ConvertModal: React.FC<ConvertProps> = ({ lead, onClose, onConverted }) => {
  const [step, setStep]       = useState(1);
  const [saving, setSaving]   = useState(false);
  const [loading, setLoading] = useState(false);

  const [savedContactId, setSavedContactId] = useState<number | null>(lead.deal?.contact_id ?? null);
  const [savedOrgId,     setSavedOrgId]     = useState<number | null>(lead.deal?.organization_id ?? null);

  const parts = lead.name.trim().split(' ');
  const specialty = (lead.specialty_interest ?? []).slice(0, 2).join('/') || 'General';

  const [contact, setContact] = useState({
    first_name: parts[0] ?? '',
    last_name:  parts.slice(1).join(' '),
    phone:      lead.mobile ?? lead.phone ?? '',
    email:      lead.email ?? '',
    designation:'Proprietor',
    is_primary: true,
  });
  const [org, setOrg] = useState({
    name:                lead.company ?? '',
    industry:            lead.business_type ?? '',
    territory:           lead.state ?? '',
    gst_number:          lead.gst_number ?? '',
    drug_licence_number: lead.drug_licence_number ?? '',
    verify_later:        false,
  });
  const [deal, setDeal] = useState({
    title:               lead.deal?.title ?? `${lead.company ?? lead.name} — ${specialty} Range`,
    value:               lead.expected_order_value?.toString() ?? '',
    stage:               lead.deal?.stage ?? 'prospecting',
    probability:         50,
    expected_close_date: lead.expected_close_date ?? '',
  });

  // Auto-fill from existing contact/org when re-opening for an already-converted lead
  useEffect(() => {
    const contactId = lead.deal?.contact_id;
    const orgId     = lead.deal?.organization_id;
    if (!contactId && !orgId) return;
    setLoading(true);
    Promise.all([
      contactId ? fetchContact(contactId) : Promise.resolve(null),
      orgId     ? fetchOrganization(orgId) : Promise.resolve(null),
    ]).then(([existingCtc, existingOrg]) => {
      if (existingCtc) {
        const nameParts = (existingCtc.name ?? '').split(' ');
        setContact({
          first_name:  nameParts[0] ?? '',
          last_name:   nameParts.slice(1).join(' '),
          phone:       existingCtc.phone ?? '',
          email:       existingCtc.email ?? '',
          designation: existingCtc.designation ?? 'Proprietor',
          is_primary:  existingCtc.is_primary ?? true,
        });
      }
      if (existingOrg) {
        setOrg({
          name:                existingOrg.name ?? '',
          industry:            existingOrg.industry ?? '',
          territory:           existingOrg.territory ?? '',
          gst_number:          existingOrg.gst_number ?? '',
          drug_licence_number: existingOrg.drug_licence_number ?? '',
          verify_later:        existingOrg.drug_licence_status === 'unverified',
        });
      }
    }).finally(() => setLoading(false));
  }, []);

  const handleNext = async () => {
    setSaving(true);
    try {
      if (step === 1) {
        const contactData = {
          name:        `${contact.first_name} ${contact.last_name}`.trim(),
          phone:       contact.phone,
          email:       contact.email,
          designation: contact.designation,
          is_primary:  contact.is_primary,
        };
        const saved = savedContactId
          ? await updateContact(savedContactId, contactData)
          : await createContact(contactData);
        setSavedContactId(saved.id);
        setStep(2);
      } else if (step === 2) {
        const orgData = {
          name:                org.name,
          industry:            org.industry,
          territory:           org.territory,
          gst_number:          org.gst_number,
          drug_licence_number: org.drug_licence_number,
          drug_licence_status: org.verify_later ? 'unverified' : 'verified',
        };
        const savedOrg = savedOrgId
          ? await updateOrganization(savedOrgId, orgData)
          : await createOrganization(orgData);
        setSavedOrgId(savedOrg.id);
        if (savedContactId) {
          await updateContact(savedContactId, { organization_id: savedOrg.id });
        }
        setStep(3);
      }
    } catch {
      alert('Failed to save. Please try again.');
    } finally { setSaving(false); }
  };

  const handleConvert = async () => {
    setSaving(true);
    try {
      const dealData = {
        title:               deal.title,
        value:               parseFloat(deal.value) || 0,
        stage:               deal.stage,
        probability:         deal.probability,
        expected_close_date: deal.expected_close_date || undefined,
        contact_id:          savedContactId,
        organization_id:     savedOrgId,
        lead_id:             lead.id,
      };
      if (lead.deal?.id) {
        await updateDeal(lead.deal.id, dealData);
      } else {
        await createDeal(dealData);
      }
      // Route through DealStageApplier (history + event + dual-write)
      // rather than a raw field set, same as every other stage change.
      // A no-op (lead already qualified) is handled by the applier itself.
      await patchLeadStatus(lead.id, { deal_stage: 'qualified' });
      onConverted();
    } catch {
      alert('Failed to save deal. Please try again.');
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-box" style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <div>
            <h2 className="modal-title">Convert Lead</h2>
            <p className="modal-sub">{lead.name} → Contact + Org + Deal</p>
          </div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {loading && (
          <div style={{ padding: '12px 24px', fontSize: 13, color: 'var(--text-muted)' }}>
            Loading existing data…
          </div>
        )}

        {/* Step bar */}
        <div className="convert-steps">
          {['Contact', 'Organization', 'Deal'].map((s, i) => (
            <React.Fragment key={s}>
              <div className={`convert-step${step === i + 1 ? ' cvt-active' : step > i + 1 ? ' cvt-done' : ''}`}>
                <div className="convert-step-circle">{step > i + 1 ? '✓' : i + 1}</div>
                <span className="convert-step-label">{s}</span>
              </div>
              {i < 2 && <div className={`convert-step-line${step > i + 1 ? ' cvt-line-done' : ''}`} />}
            </React.Fragment>
          ))}
        </div>

        <div className="convert-body">
          {step === 1 && (
            <>
              <h3 className="convert-section-title">Confirm Contact Details</h3>
              <div className="convert-grid">
                <div className="convert-field">
                  <label>First Name</label>
                  <input className="lead-form-input" value={contact.first_name} onChange={e => setContact(c => ({ ...c, first_name: e.target.value }))} />
                </div>
                <div className="convert-field">
                  <label>Last Name</label>
                  <input className="lead-form-input" value={contact.last_name} onChange={e => setContact(c => ({ ...c, last_name: e.target.value }))} />
                </div>
                <div className="convert-field">
                  <label>Phone</label>
                  <input className="lead-form-input" value={contact.phone} onChange={e => setContact(c => ({ ...c, phone: e.target.value }))} />
                </div>
                <div className="convert-field">
                  <label>Email</label>
                  <input className="lead-form-input" type="email" value={contact.email} onChange={e => setContact(c => ({ ...c, email: e.target.value }))} />
                </div>
                <div className="convert-field">
                  <label>Designation</label>
                  <input className="lead-form-input" value={contact.designation} onChange={e => setContact(c => ({ ...c, designation: e.target.value }))} />
                </div>
                <div className="convert-field" style={{ display: 'flex', alignItems: 'center', gap: 10, paddingTop: 20 }}>
                  <button
                    type="button"
                    className={`cvt-toggle${contact.is_primary ? ' cvt-toggle-on' : ''}`}
                    onClick={() => setContact(c => ({ ...c, is_primary: !c.is_primary }))}
                  >
                    <span className="cvt-toggle-knob" />
                  </button>
                  <span style={{ fontSize: 13 }}>Primary contact</span>
                </div>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <h3 className="convert-section-title">Confirm Organization</h3>
              <div className="convert-grid">
                <div className="convert-field">
                  <label>Organization Name</label>
                  <input className="lead-form-input" value={org.name} onChange={e => setOrg(o => ({ ...o, name: e.target.value }))} />
                </div>
                <div className="convert-field">
                  <label>Industry</label>
                  <input className="lead-form-input" value={org.industry} onChange={e => setOrg(o => ({ ...o, industry: e.target.value }))} />
                </div>
                <div className="convert-field">
                  <label>Territory</label>
                  <input className="lead-form-input" value={org.territory} onChange={e => setOrg(o => ({ ...o, territory: e.target.value }))} />
                </div>
                <div className="convert-field">
                  <label>GST No.</label>
                  <input className="lead-form-input" value={org.gst_number} onChange={e => setOrg(o => ({ ...o, gst_number: e.target.value }))} />
                </div>
                <div className="convert-field convert-field-full">
                  <label>Drug Licence No.</label>
                  <input className="lead-form-input" value={org.drug_licence_number} onChange={e => setOrg(o => ({ ...o, drug_licence_number: e.target.value }))} />
                </div>
                <div className="convert-field convert-field-full">
                  <label className="cvt-verify-later">
                    <input type="checkbox" checked={org.verify_later} onChange={e => setOrg(o => ({ ...o, verify_later: e.target.checked }))} />
                    Drug licence will be verified later
                  </label>
                </div>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <h3 className="convert-section-title">Create Deal</h3>
              <div className="convert-grid">
                <div className="convert-field convert-field-full">
                  <label>Deal Title</label>
                  <input className="lead-form-input" value={deal.title} onChange={e => setDeal(d => ({ ...d, title: e.target.value }))} />
                </div>
                <div className="convert-field">
                  <label>Deal Value (₹/mo)</label>
                  <input className="lead-form-input" type="number" value={deal.value} onChange={e => setDeal(d => ({ ...d, value: e.target.value }))} />
                </div>
                <div className="convert-field">
                  <label>Stage</label>
                  <select className="lead-form-input" value={deal.stage} onChange={e => setDeal(d => ({ ...d, stage: e.target.value }))}>
                    <option value="prospecting">OPEN</option>
                    <option value="negotiation">NEGOTIATION</option>
                    <option value="proposal">PROPOSAL</option>
                    <option value="closed_won">WON</option>
                    <option value="closed_lost">LOST</option>
                  </select>
                </div>
                <div className="convert-field convert-field-full">
                  <label>Probability: {deal.probability}%</label>
                  <input type="range" min={0} max={100} value={deal.probability}
                    onChange={e => setDeal(d => ({ ...d, probability: parseInt(e.target.value) }))}
                    className="cvt-slider" />
                </div>
                <div className="convert-field convert-field-full">
                  <label>Expected Close</label>
                  <input className="lead-form-input" type="date" value={deal.expected_close_date} onChange={e => setDeal(d => ({ ...d, expected_close_date: e.target.value }))} />
                </div>
              </div>
            </>
          )}
        </div>

        <div className="convert-footer">
          {step > 1
            ? <button className="btn btn-secondary" onClick={() => setStep(s => s - 1)} disabled={saving}>← Back</button>
            : <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          }
          {step < 3
            ? <button className="btn btn-primary" onClick={handleNext} disabled={saving}>
                {saving ? 'Saving…' : 'Next →'}
              </button>
            : <button className="btn btn-primary" onClick={handleConvert} disabled={saving}>
                {saving ? 'Saving…' : 'Convert Lead ✓'}
              </button>
          }
        </div>
      </div>
    </div>
  );
};

/* ── Unified Timeline ───────────────────────────────────────────────────── */
type PortalInquiryData = {
  id: string;
  title: string;
  source_name: string;         // display name for the source pill (IndiaMART etc.)
  product?: string | null;     // QUERY_PRODUCT_NAME
  message?: string | null;     // QUERY_MESSAGE
  meta?: Record<string, unknown> | null;
  timestamp: string;
};

type FeedItem =
  | { kind: 'activity';        ts: string; data: Activity }
  | { kind: 'call';            ts: string; data: InteractionEvent }
  | { kind: 'interaction';     ts: string; data: TimelineEntry }
  | { kind: 'note';            ts: string; data: Note }
  | { kind: 'task';            ts: string; data: Task }
  | { kind: 'portal_inquiry';  ts: string; data: PortalInquiryData };

type TLFilter = 'all' | 'calls' | 'notes' | 'tasks' | 'status' | 'ai_calls' | 'ai_wa';

const TL_FILTERS: { key: TLFilter; label: string }[] = [
  { key: 'all',      label: 'All' },
  { key: 'status',   label: 'Status Changes' },
  { key: 'calls',    label: 'Calls' },
  { key: 'ai_calls', label: 'AI Calls' },
  { key: 'ai_wa',    label: 'AI WhatsApp' },
  { key: 'notes',    label: 'Notes' },
  { key: 'tasks',    label: 'Tasks' },
];

const fmtDate = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const fmtDuration = (s: number | null) => {
  if (!s) return '';
  const m = Math.floor(s / 60), sec = s % 60;
  return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
};

const SENTIMENT_COLOR: Record<string, string> = {
  positive: '#16a34a', neutral: '#64748b', negative: '#dc2626', frustrated: '#ea580c',
};

const ACTIVITY_ICON: Record<string, string> = {
  created: '✦', updated: '✎', status_changed: '⇄', stage_changed: '⇄',
  assigned: '👤', note_added: '📝', task_added: '☑', task_completed: '✓', voice_call: '📞',
};

const UnifiedTimeline: React.FC<{ leadId: number; refreshKey?: number }> = ({ leadId, refreshKey = 0 }) => {
  const [items, setItems]     = useState<FeedItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter]   = useState<TLFilter>('all');
  const [expandedTranscripts, setExpandedTranscripts] = useState<Set<string>>(new Set());

  const [addingTask, setAddingTask]   = useState(false);
  const [taskTitle, setTaskTitle]     = useState('');
  const [taskDue, setTaskDue]         = useState('');
  const [taskPriority, setTaskPriority] = useState<'low'|'medium'|'high'>('medium');
  const [savingTask, setSavingTask]   = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    fetchLeadTimeline(leadId).then((items) => {
      const feed: FeedItem[] = items.map(item => {
        if (item.kind === 'activity') {
          return {
            kind: 'activity' as const,
            ts: item.timestamp ?? '',
            data: {
              id: parseInt(item.id.split(':')[1]) || 0,
              subject_type: 'App\\Models\\Lead',
              subject_id: leadId,
              type: (item.type ?? 'updated') as Activity['type'],
              description: item.description ?? '',
              user: item.user ?? undefined,
              created_at: item.timestamp ?? '',
              updated_at: item.timestamp ?? '',
            } as Activity,
          };
        }
        if (item.kind === 'note') {
          return {
            kind: 'note' as const,
            ts: item.timestamp ?? '',
            data: {
              id: item.note_id!,
              noteable_type: 'App\\Models\\Lead',
              noteable_id: leadId,
              content: item.content ?? '',
              user: item.user ?? undefined,
              created_at: item.timestamp ?? '',
              updated_at: item.timestamp ?? '',
            } as Note,
          };
        }
        if (item.kind === 'task') {
          return {
            kind: 'task' as const,
            ts: item.timestamp ?? '',
            data: {
              id: item.task_id!,
              taskable_type: 'App\\Models\\Lead',
              taskable_id: leadId,
              title: item.title ?? '',
              due_date: item.due_date ?? undefined,
              priority: item.priority ?? undefined,
              is_done: item.is_done ?? false,
              is_auto: item.is_auto,
              assigned_user: item.assigned_user ?? undefined,
              creator: item.creator ?? undefined,
              created_at: item.timestamp ?? '',
              updated_at: item.timestamp ?? '',
            } as Task,
          };
        }
        if (item.kind === 'call') {
          return {
            kind: 'call' as const,
            ts: item.timestamp ?? '',
            data: {
              id: parseInt(item.id.split(':')[1]) || 0,
              event_type: item.event_type ?? item.title ?? '',
              summary: item.summary ?? '',
              duration: item.duration_seconds ?? null,
              recording_url: item.recording_url ?? null,
              metadata: {
                sentiment: (item.sentiment ?? null) as 'positive' | 'neutral' | 'negative' | 'frustrated' | null,
                was_booked: item.was_booked ?? false,
                booking_id: null,
                booking_time: null,
              },
              timestamp: item.timestamp ?? '',
            } as InteractionEvent,
          };
        }
        // 2026-07-22 — Portal inquiry (IndiaMART etc.) — buyer intent card
        if ((item as { kind?: string }).kind === 'portal_inquiry') {
          const it = item as unknown as {
            id: string; title?: string; timestamp?: string;
            product?: string | null; message?: string | null;
            meta?: Record<string, unknown> | null;
          };
          return {
            kind: 'portal_inquiry' as const,
            ts: it.timestamp ?? '',
            data: {
              id: it.id,
              title: it.title ?? 'Portal Inquiry',
              source_name: (it.title ?? 'Portal').replace(/\s*Inquiry\s*$/i, ''),
              product: it.product,
              message: it.message,
              meta: it.meta ?? null,
              timestamp: it.timestamp ?? '',
            },
          };
        }
        // kind === 'interaction'
        return {
          kind: 'interaction' as const,
          ts: item.timestamp ?? '',
          data: item,
        };
      });
      setItems(feed);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [leadId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const handleDeleteNote = async (noteId: number) => {
    await deleteNote('leads', leadId, noteId);
    setItems(prev => prev.filter(i => !(i.kind === 'note' && i.data.id === noteId)));
  };

  const handleToggleTask = async (taskId: number, isDone: boolean) => {
    const updated = await updateTask('leads', leadId, taskId, { is_done: !isDone });
    setItems(prev => prev.map(i => i.kind === 'task' && i.data.id === taskId ? { ...i, data: updated } : i));
  };

  const handleAddTask = async () => {
    if (!taskTitle.trim()) return;
    setSavingTask(true);
    try {
      const created: Task = await createTask('leads', leadId, {
        title: taskTitle.trim(),
        due_date: taskDue || undefined,
        priority: taskPriority,
      });
      setItems(prev => [{ kind: 'task', ts: created.created_at, data: created }, ...prev]);
      setTaskTitle(''); setTaskDue(''); setTaskPriority('medium'); setAddingTask(false);
    } catch {} finally { setSavingTask(false); }
  };

  const filtered = items.filter(i => {
    if (filter === 'all')      return true;
    if (filter === 'calls')    return i.kind === 'call' || (i.kind === 'interaction' && (i.data.channel === 'voice_ai' || i.data.channel === 'voice_human'));
    if (filter === 'ai_calls') return i.kind === 'interaction' && i.data.channel === 'voice_ai';
    if (filter === 'ai_wa')    return i.kind === 'interaction' && i.data.channel === 'whatsapp';
    if (filter === 'notes')    return i.kind === 'note';
    if (filter === 'tasks')    return i.kind === 'task';
    if (filter === 'status') {
      return i.kind === 'activity' &&
        ['status_changed', 'stage_changed', 'assigned', 'created'].includes((i.data as Activity).type);
    }
    return true;
  });

  return (
    <div>
      {/* Filter strip */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        {TL_FILTERS.map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            style={{
              padding: '5px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600,
              border: '1.5px solid',
              borderColor: filter === f.key ? '#7c3aed' : 'var(--border)',
              background: filter === f.key ? '#ede9fe' : '#fff',
              color: filter === f.key ? '#7c3aed' : 'var(--text-muted)',
              cursor: 'pointer',
            }}
          >
            {f.label}
          </button>
        ))}
        {(filter === 'tasks' || filter === 'all') && !addingTask && (
          <button
            onClick={() => setAddingTask(true)}
            style={{
              marginLeft: 'auto', padding: '5px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600,
              border: '1.5px solid #16a34a', background: '#dcfce7', color: '#166534', cursor: 'pointer',
            }}
          >
            + Add Task
          </button>
        )}
      </div>

      {/* Inline Add Task form */}
      {addingTask && (
        <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 10, padding: '14px 16px', marginBottom: 16 }}>
          <div style={{ fontWeight: 600, fontSize: 13, color: '#166534', marginBottom: 10 }}>New Task</div>
          <input
            className="form-input"
            placeholder="Task title *"
            value={taskTitle}
            onChange={e => setTaskTitle(e.target.value)}
            style={{ width: '100%', marginBottom: 8 }}
            autoFocus
            onKeyDown={e => e.key === 'Enter' && handleAddTask()}
          />
          <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
            <input
              type="date"
              className="form-input"
              value={taskDue}
              onChange={e => setTaskDue(e.target.value)}
              style={{ flex: 1 }}
            />
            <select
              className="form-input"
              value={taskPriority}
              onChange={e => setTaskPriority(e.target.value as any)}
              style={{ flex: 1 }}
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn-secondary" onClick={() => { setAddingTask(false); setTaskTitle(''); setTaskDue(''); }} style={{ fontSize: 13 }}>Cancel</button>
            <button
              className="btn-primary"
              onClick={handleAddTask}
              disabled={savingTask || !taskTitle.trim()}
              style={{ fontSize: 13, minWidth: 100 }}
            >
              {savingTask ? 'Saving…' : 'Add Task'}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="spinner">Loading…</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)', fontSize: 14 }}>
          No {filter === 'all' ? '' : filter} items yet.
        </div>
      ) : (
        <div style={{ position: 'relative' }}>
          {/* Vertical line */}
          <div style={{ position: 'absolute', left: 15, top: 8, bottom: 8, width: 2, background: 'var(--border)' }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {filtered.map((item, idx) => (
              <div key={`${item.kind}-${item.data.id}-${idx}`} style={{ display: 'flex', gap: 16, paddingBottom: 20 }}>
                {/* Dot */}
                <div style={{
                  flexShrink: 0, width: 32, height: 32, borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 13, zIndex: 1,
                  background:
                    item.kind === 'interaction' && item.data.channel === 'voice_ai'    ? '#ede9fe' :
                    item.kind === 'interaction' && item.data.channel === 'voice_human' ? '#e0f2fe' :
                    item.kind === 'interaction' && item.data.channel === 'whatsapp'    ? '#d1fae5' :
                    item.kind === 'interaction' && item.data.channel === 'chat'        ? '#f3e8ff' :
                    item.kind === 'call'           ? '#dbeafe' :
                    item.kind === 'note'           ? '#fef9c3' :
                    item.kind === 'task'           ? '#dcfce7' :
                    item.kind === 'portal_inquiry' ? '#ffedd5' : '#f1f5f9',
                  border: '2px solid',
                  borderColor:
                    item.kind === 'interaction' && item.data.channel === 'voice_ai'    ? '#c4b5fd' :
                    item.kind === 'interaction' && item.data.channel === 'voice_human' ? '#7dd3fc' :
                    item.kind === 'interaction' && item.data.channel === 'whatsapp'    ? '#6ee7b7' :
                    item.kind === 'interaction' && item.data.channel === 'chat'        ? '#c084fc' :
                    item.kind === 'call'           ? '#93c5fd' :
                    item.kind === 'note'           ? '#fde047' :
                    item.kind === 'task'           ? '#86efac' :
                    item.kind === 'portal_inquiry' ? '#fb923c' : 'var(--border)',
                }}>
                  {item.kind === 'interaction' && item.data.channel === 'voice_ai'    ? '🤖' :
                   item.kind === 'interaction' && item.data.channel === 'voice_human' ? '📱' :
                   item.kind === 'interaction' && item.data.channel === 'whatsapp'    ? '💬' :
                   item.kind === 'interaction' && item.data.channel === 'chat'        ? '💭' :
                   item.kind === 'call'           ? '📞' :
                   item.kind === 'note'           ? '📝' :
                   item.kind === 'task'           ? (item.data.is_done ? '✓' : '☐') :
                   item.kind === 'portal_inquiry' ? '🛒' :
                   ACTIVITY_ICON[(item.data as Activity).type] ?? '·'}
                </div>

                {/* Content */}
                <div style={{ flex: 1, paddingTop: 4 }}>
                  {item.kind === 'activity' && (
                    <div>
                      <div style={{ fontSize: 14, color: 'var(--text)', lineHeight: 1.5 }}>{item.data.description}</div>
                      {item.data.user && (
                        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>by {item.data.user.name}</div>
                      )}
                    </div>
                  )}

                  {item.kind === 'portal_inquiry' && (
                    <div style={{
                      background: '#fff7ed',
                      border: '1px solid #fed7aa',
                      borderRadius: 8,
                      padding: '10px 14px',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        <span style={{
                          fontSize: 11, fontWeight: 700, letterSpacing: 0.3,
                          textTransform: 'uppercase', padding: '2px 8px', borderRadius: 20,
                          background: '#fb923c', color: '#fff',
                        }}>
                          {item.data.source_name}
                        </span>
                        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--navy)' }}>
                          Buyer Inquiry
                        </span>
                      </div>
                      {item.data.product && (
                        <div style={{ marginBottom: 6 }}>
                          <span style={{ fontSize: 11, fontWeight: 700, color: '#9a3412', textTransform: 'uppercase', letterSpacing: 0.3 }}>
                            Product interested
                          </span>
                          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--navy)', marginTop: 2 }}>
                            {item.data.product}
                          </div>
                        </div>
                      )}
                      {item.data.message && (
                        <div style={{ marginTop: 6 }}>
                          <span style={{ fontSize: 11, fontWeight: 700, color: '#9a3412', textTransform: 'uppercase', letterSpacing: 0.3 }}>
                            Message
                          </span>
                          <div style={{ fontSize: 13, color: 'var(--text)', marginTop: 2, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                            {item.data.message}
                          </div>
                        </div>
                      )}
                      {(() => {
                        const meta = item.data.meta ?? {};
                        const parts = [meta.company, meta.city, meta.state, meta.pincode]
                          .filter((v): v is string => typeof v === 'string' && v.length > 0);
                        if (parts.length === 0) return null;
                        return (
                          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8, borderTop: '1px dashed #fdba74', paddingTop: 6 }}>
                            {parts.join(' · ')}
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {item.kind === 'call' && (
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--navy)' }}>
                          {item.data.event_type.replace(/_/g, ' ')}
                        </span>
                        {item.data.duration && (
                          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{fmtDuration(item.data.duration)}</span>
                        )}
                        {item.data.metadata?.sentiment && (
                          <span style={{
                            fontSize: 11, fontWeight: 600, padding: '1px 8px', borderRadius: 20,
                            background: '#f1f5f9', color: SENTIMENT_COLOR[item.data.metadata.sentiment] ?? '#64748b',
                          }}>
                            {item.data.metadata.sentiment}
                          </span>
                        )}
                        {item.data.metadata?.was_booked && (
                          <span style={{ fontSize: 11, fontWeight: 600, padding: '1px 8px', borderRadius: 20, background: '#dcfce7', color: '#166534' }}>
                            Booked
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>{item.data.summary}</div>
                      {item.data.recording_url ? (
                        <a href={item.data.recording_url} target="_blank" rel="noreferrer"
                           style={{ fontSize: 12, color: '#7c3aed', marginTop: 4, display: 'inline-block' }}>
                          ▶ Recording
                        </a>
                      ) : item.data.metadata?.sim_call_id ? (
                        <SimCallAudioPlayer simCallId={item.data.metadata.sim_call_id as number} />
                      ) : null}
                    </div>
                  )}

                  {item.kind === 'interaction' && (() => {
                    const e = item.data;
                    const channelLabel =
                      e.channel === 'voice_ai'    ? 'AI Voice Call' :
                      e.channel === 'voice_human' ? 'Human Call' :
                      e.channel === 'whatsapp'    ? 'WhatsApp' :
                      e.channel === 'call_meta'   ? 'Call Attempt' :
                      e.channel === 'portal'      ? 'Portal Inquiry' :
                      e.channel === 'chat'        ? 'Web Chat' : 'Interaction';
                    const isCallMeta = e.channel === 'call_meta';
                    const fullText = e.transcript_text ?? '';
                    const short = fullText.length > 240 ? fullText.slice(0, 240) + '…' : fullText;
                    const expanded = expandedTranscripts.has(e.id);
                    return (
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
                          <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--navy)' }}>{channelLabel}</span>
                          <span style={{ fontSize: 11, fontWeight: 600, padding: '1px 8px', borderRadius: 20, background: '#ede9fe', color: '#7c3aed' }}>
                            {e.direction === 'inbound' ? 'Inbound' : 'Outbound'}
                          </span>
                          {e.duration_seconds ? <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{fmtDuration(e.duration_seconds)}</span> : null}
                          {e.sentiment && (
                            <span style={{ fontSize: 11, fontWeight: 600, padding: '1px 8px', borderRadius: 20, background: '#f1f5f9', color: SENTIMENT_COLOR[e.sentiment] ?? '#64748b' }}>
                              {e.sentiment}
                            </span>
                          )}
                        </div>

                        {fullText && (
                          <div style={{ background: '#f8fafc', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', marginBottom: 6 }}>
                            <div style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', marginBottom: 3 }}>{isCallMeta ? 'CALL STATUS' : 'TRANSCRIPT'}</div>
                            {e.turns && expanded ? (
                              e.turns.map((t, i) => (
                                <div key={i} style={{ fontSize: 13, marginBottom: 2 }}>
                                  <strong style={{ color: '#7c3aed' }}>{t.speaker}:</strong> {t.text}
                                </div>
                              ))
                            ) : (
                              <div style={{ fontSize: 13, color: 'var(--text)', whiteSpace: 'pre-wrap' }}>{expanded ? fullText : short}</div>
                            )}
                            {fullText.length > 240 && (
                              <button
                                onClick={() => setExpandedTranscripts(prev => {
                                  const next = new Set(prev);
                                  if (next.has(e.id)) { next.delete(e.id); } else { next.add(e.id); }
                                  return next;
                                })}
                                style={{ marginTop: 6, fontSize: 12, color: '#7c3aed', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                              >
                                {expanded ? 'Show less' : 'Show full transcript'}
                              </button>
                            )}
                          </div>
                        )}

                        {e.summary && (
                          <div style={{ background: '#ede9fe', border: '1px solid #c4b5fd', borderRadius: 8, padding: '8px 12px' }}>
                            <div style={{ fontSize: 11, fontWeight: 600, color: '#7c3aed', marginBottom: 3 }}>SUMMARY</div>
                            <div style={{ fontSize: 13, color: '#4c1d95', whiteSpace: 'pre-wrap' }}>{e.summary}</div>
                          </div>
                        )}

                        {e.recording_url ? (
                          <a href={e.recording_url} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: '#7c3aed', marginTop: 4, display: 'inline-block' }}>▶ Recording</a>
                        ) : (e.has_audio && e.sim_call_id) ? (
                          <SimCallAudioPlayer simCallId={e.sim_call_id} />
                        ) : null}
                      </div>
                    );
                  })()}

                  {item.kind === 'note' && (
                    <div style={{ background: '#fefce8', border: '1px solid #fde047', borderRadius: 8, padding: '10px 12px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div style={{ fontSize: 14, color: 'var(--text)', whiteSpace: 'pre-wrap', flex: 1 }}>{item.data.content}</div>
                        <button
                          onClick={() => handleDeleteNote(item.data.id)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 14, marginLeft: 8, padding: 2, flexShrink: 0 }}
                          title="Delete note"
                        >✕</button>
                      </div>
                      {item.data.user && (
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>by {item.data.user.name}</div>
                      )}
                    </div>
                  )}

                  {item.kind === 'task' && (
                    <div style={{
                      display: 'flex', alignItems: 'flex-start', gap: 10,
                      background: item.data.is_done ? '#f0fdf4' : '#fff',
                      border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px',
                    }}>
                      <button
                        onClick={() => handleToggleTask(item.data.id, item.data.is_done)}
                        style={{
                          flexShrink: 0, width: 20, height: 20, borderRadius: 4,
                          border: '2px solid', cursor: 'pointer',
                          borderColor: item.data.is_done ? '#16a34a' : '#94a3b8',
                          background: item.data.is_done ? '#16a34a' : '#fff',
                          color: '#fff', fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                      >
                        {item.data.is_done ? '✓' : ''}
                      </button>
                      <div style={{ flex: 1 }}>
                        <div style={{
                          fontSize: 14, color: 'var(--navy)', fontWeight: 500,
                          textDecoration: item.data.is_done ? 'line-through' : 'none',
                          opacity: item.data.is_done ? 0.6 : 1,
                        }}>
                          {item.data.title}
                        </div>
                        <div style={{ display: 'flex', gap: 10, marginTop: 3, flexWrap: 'wrap' }}>
                          {item.data.due_date && (
                            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                              Due: {item.data.due_date}{item.data.due_time ? ` ${item.data.due_time}` : ''}
                            </span>
                          )}
                          {item.data.priority && (
                            <span style={{
                              fontSize: 11, fontWeight: 600, padding: '1px 7px', borderRadius: 20,
                              background: item.data.priority === 'high' ? '#fee2e2' : item.data.priority === 'medium' ? '#fef9c3' : '#f1f5f9',
                              color:      item.data.priority === 'high' ? '#dc2626' : item.data.priority === 'medium' ? '#854d0e' : '#64748b',
                            }}>
                              {item.data.priority}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>{fmtDate(item.ts)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/* ── LeadDetail ─────────────────────────────────────────────────────────── */
const LeadDetail: React.FC = () => {
  const { id: idHash } = useParams<{ id: string }>();
  const numId          = decodeId(idHash) ?? 0;
  const history        = useHistory();
  const location       = useLocation();
  // Support deep-linking to a specific tab: /leads/{hash}?tab=overview
  // Default is Timeline so reps land on the latest activity (notes, calls,
  // messages) instead of static profile fields they already know. The Tasks
  // page's deep link to Timeline is now redundant but harmless.
  const initialTab: Tab = (() => {
    const raw = new URLSearchParams(location.search).get('tab');
    return raw === 'qualification' || raw === 'timeline' || raw === 'overview'
      ? (raw as Tab) : 'timeline';
  })();
  const [lead, setLead]           = useState<Lead | null>(null);
  const [agents, setAgents]       = useState<User[]>([]);
  const [tab, setTab]             = useState<Tab>(initialTab);
  const [error, setError]         = useState('');
  const [showConvert, setShowConvert] = useState(false);
  const [showStatusEditor, setShowStatusEditor] = useState(false);
  const [quickNote, setQuickNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [noteCount, setNoteCount] = useState(0);
  const quickNoteRef = useRef<HTMLTextAreaElement>(null);

  const reload = () =>
    fetchLead(numId).then(setLead).catch(() => setError('Lead not found'));

  useEffect(() => {
    reload();
    fetchUsersList().then(setAgents).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idHash]);

  if (error) return <div className="ld-page"><p className="ld-empty">Lead not found.</p></div>;
  if (!lead)  return <div className="ld-page"><div className="spinner">Loading…</div></div>;

  const score      = QUAL_ITEMS.filter(q => !!(lead as any)[q.key]).length;
  const temp       = lead.temperature ?? 'COLD';
  const tempColor  = TEMP_COLOR[temp] ?? '#64748B';

  const patchLead = async (data: object) => {
    const updated = await updateLead(lead.id, data);
    setLead(updated);
  };

  const handleQualToggle = (field: string, current: boolean) =>
    patchLead({ [field]: !current });

  const handleQuickNote = async () => {
    if (!quickNote.trim()) return;
    setSavingNote(true);
    try {
      await createNote('leads', lead.id, quickNote.trim());
      setQuickNote('');
      setNoteCount(n => n + 1);
      if (tab !== 'timeline') setTab('timeline');
    } finally { setSavingNote(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete lead "${lead.name}"?`)) return;
    await deleteLead(lead.id);
    history.push('/leads');
  };

  return (
    <div className="ld-page">
      <button className="breadcrumb-back" onClick={() => history.push('/leads')}>
        ← Back to Leads
      </button>

      <div className="ld-layout">
        {/* ── MAIN COLUMN ─────────────────────────────── */}
        <div className="ld-main">

          {/* Header card */}
          <div className="ld-header-card">
            <div className="ld-header-top">
              <div className="ld-avatar">{initials(lead.name)}</div>

              <div className="ld-hero">
                <div className="ld-hero-row">
                  <h1 className="ld-name">{lead.name}</h1>
                  <span className="temp-pill" style={{ background: tempColor }}>{temp}</span>
                  <LeadStatusBadge
                    status={(lead.deal_stage ?? 'new') as DealStageValue}
                    lostReason={lead.lost_reason ?? null}
                    onClick={() => setShowStatusEditor(true)}
                  />
                  <ReachabilityBadge
                    reachability={lead.reachability ?? 'unattempted'}
                    attempts={lead.reachability_attempts}
                  />
                  <RelationshipBadge relationship={lead.relationship ?? 'prospect'} />
                  <IssueChips issues={lead.issues} />
                </div>
                <div className="ld-company">
                  {[lead.company, lead.city, lead.state].filter(Boolean).join(' · ')}
                </div>
                <div className="ld-contact-row">
                  {(lead.mobile || lead.phone) && (
                    <a className="ld-phone" href={`tel:${lead.mobile ?? lead.phone}`}>
                      +{(lead.mobile ?? lead.phone ?? '').replace(/^\+/, '')}
                    </a>
                  )}
                  {lead.whatsapp_number && (
                    <span className="ld-wa-dot" title="WhatsApp available">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.18-.01-.382-.012-.57-.012-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                    </span>
                  )}
                  {lead.email && <span className="ld-email">{lead.email}</span>}
                </div>
              </div>

              <div className="ld-header-meta">
                <span className="source-pill">{(lead.source ?? '').toUpperCase()}</span>
                <span className="ld-time">{timeAgo(lead.last_touch_at ?? lead.updated_at)}</span>
              </div>
            </div>

            {/* Action buttons */}
            <div className="ld-actions">
              <button className="ld-btn ld-btn-dial">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.01L6.6 10.8z"/>
                </svg>
                Dial
              </button>
              {(() => {
                // If this lead is on a tenant we've wired up to the
                // console.automately.net BSP, the WhatsApp button jumps
                // straight to the vendor's chat window in a new tab.
                // Otherwise it stays as a stub (matches historical
                // behaviour — the button has never had its own action).
                const vendorUrl = whatsappVendorChatUrl(lead);
                return (
                  <button
                    className="ld-btn ld-btn-wa"
                    title={vendorUrl ? 'Open chat in WhatsApp vendor' : undefined}
                    onClick={() => {
                      if (vendorUrl) {
                        window.open(vendorUrl, '_blank', 'noopener,noreferrer');
                      }
                    }}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.18-.01-.382-.012-.57-.012-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                    WhatsApp
                  </button>
                );
              })()}
              <button className="ld-btn ld-btn-note" onClick={() => {
                setTab('timeline');
                setTimeout(() => quickNoteRef.current?.focus(), 50);
              }}>+ Note</button>
              <button className="ld-btn ld-btn-convert" onClick={() => setShowConvert(true)}>
                {lead.deal ? 'Edit Conversion →' : 'Convert →'}
              </button>
              <button className="ld-btn ld-btn-more" onClick={() => history.push(`/leads/${encodeId(lead.id)}/edit`)}>Edit</button>
              <button className="ld-btn ld-btn-danger" onClick={handleDelete}>Delete</button>
            </div>

            {/* Tabs */}
            <div className="ld-tabs">
              {([
                ['overview',      'Overview'],
                ['qualification', 'Qualification'],
                ['timeline',      'Timeline'],
              ] as [Tab, string][]).map(([t, label]) => (
                <button key={t} className={`ld-tab${tab === t ? ' ld-tab-active' : ''}`} onClick={() => setTab(t)}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Tab bodies */}
          <div className="ld-tab-body">

            {tab === 'overview' && (
              <div className="ld-overview">
                <div className="ld-section">
                  <h3 className="ld-section-title">Contact Info</h3>
                  <div className="ld-info-grid">
                    <InfoRow label="Mobile"       value={lead.mobile ?? lead.phone} />
                    <InfoRow label="WhatsApp"      value={lead.whatsapp_number} />
                    <InfoRow label="Email"         value={lead.email} />
                    <InfoRow label="City"          value={lead.city} />
                    <InfoRow label="District"      value={lead.district} />
                    <InfoRow label="State"         value={lead.state} />
                    <div className="ld-info-row">
                      <span className="ld-info-label">Drug Licence</span>
                      <span className="ld-info-value">
                        {lead.drug_licence_number
                          ? <>{lead.drug_licence_number} <span className="dl-verified">Verified ✓</span></>
                          : <em className="ld-empty">—</em>
                        }
                      </span>
                    </div>
                    <InfoRow label="GST No." value={lead.gst_number} />
                  </div>
                </div>

                <div className="ld-section">
                  <h3 className="ld-section-title">Business Profile</h3>
                  <div className="ld-info-grid">
                    <InfoRow label="Business Type"       value={lead.business_type} />
                    <InfoRow label="Speciality Interest" value={(lead.specialty_interest ?? []).join(', ') || undefined} />
                    <InfoRow label="MOQ Estimate"        value={lead.moq_min != null && lead.moq_max != null
                      ? `₹${Number(lead.moq_min).toLocaleString('en-IN')} – ${Number(lead.moq_max).toLocaleString('en-IN')}` : undefined} />
                    <InfoRow label="Coverage Districts"  value={lead.coverage_districts} />
                    <InfoRow label="Current Supplier"    value={lead.current_supplier} />
                    <InfoRow label="MR Count"            value={lead.mr_count?.toString()} />
                    <InfoRow label="Years in Business"   value={lead.years_in_business?.toString()} />
                    <InfoRow label="ERP Software"        value={lead.erp_software} />
                  </div>
                </div>

                <div className="ld-section">
                  <h3 className="ld-section-title">Lead Origin</h3>
                  <div className="ld-info-grid">
                    <InfoRow label="Source"      value={lead.source?.toUpperCase()} />
                    <InfoRow label="Campaign"    value={lead.campaign} />
                    <InfoRow label="First Touch" value={lead.first_touch_date} />
                  </div>
                </div>
              </div>
            )}

            {tab === 'qualification' && (
              <div className="ld-qualification">
                <div className="qual-header">
                  <div>
                    <div className="qual-title">Qualification Score</div>
                    <div className="qual-sub">Check off each item as you confirm it with the lead</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span className="qual-score-big">{score}/4</span>
                    <span className="temp-pill" style={{ background: tempColor }}>{temp}</span>
                  </div>
                </div>
                <div className="qual-bar">
                  <div className="qual-bar-fill" style={{ width: `${score * 25}%`, background: tempColor }} />
                </div>
                <div className="qual-list">
                  {QUAL_ITEMS.map(item => {
                    const checked = !!(lead as any)[item.key];
                    return (
                      <div key={item.key}
                        className={`qual-item${checked ? ' qual-item-on' : ''}`}
                        onClick={() => handleQualToggle(item.key, checked)}>
                        <div className={`qual-check${checked ? ' qual-check-on' : ''}`}>
                          {checked && '✓'}
                        </div>
                        <div>
                          <div className="qual-item-label" style={{ color: checked ? 'var(--teal)' : 'var(--navy)' }}>
                            {item.label}
                          </div>
                          <div className="qual-item-desc">{item.desc}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {tab === 'timeline' && (
              <div className="ld-section">
                {lead.cumulative_summary && (
                  <div style={{ background: '#faf5ff', border: '1px solid #e9d5ff', borderRadius: 10, padding: '12px 16px', marginBottom: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: '#7c3aed', letterSpacing: 0.4 }}>AI SUMMARY</span>
                      {lead.sentiment && (
                        <span style={{ fontSize: 11, fontWeight: 600, padding: '1px 8px', borderRadius: 20,
                          background: lead.sentiment === 'positive' ? '#dcfce7' : lead.sentiment === 'negative' ? '#fee2e2' : '#f1f5f9',
                          color:      lead.sentiment === 'positive' ? '#166534' : lead.sentiment === 'negative' ? '#991b1b' : '#475569' }}>
                          {lead.sentiment}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--text)', whiteSpace: 'pre-wrap' }}>{lead.cumulative_summary}</div>
                    {lead.summary_refreshed_at && (
                      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>
                        Updated {new Date(lead.summary_refreshed_at).toLocaleString()}
                      </div>
                    )}
                  </div>
                )}
                <SummaryHistory leadId={lead.id} />
                <UnifiedTimeline leadId={lead.id} refreshKey={noteCount} />
              </div>
            )}
          </div>
        </div>

        {/* ── SIDEBAR ─────────────────────────────────── */}
        <div className="ld-sidebar">

          {/* Linked Deal (if converted) */}
          {lead.deal && (
            <div className="ld-card">
              <div className="ld-card-title">Linked Deal</div>
              <div className="ld-linked-deal-title">{lead.deal.title}</div>
              <div className="ld-linked-deal-stage">{lead.deal.stage.replace(/_/g, ' ').toUpperCase()}</div>
              <button className="btn btn-secondary" style={{ width: '100%', marginTop: 8 }}
                onClick={() => history.push(`/deals/${encodeId(lead.deal!.id)}`)}>
                Open Deal →
              </button>
            </div>
          )}

          {/* Deal Stage */}
          <div className="ld-card">
            <div className="ld-card-title">Deal Stage</div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
              <LeadStatusBadge
                status={(lead.deal_stage ?? 'new') as DealStageValue}
                lostReason={lead.lost_reason ?? null}
              />
              <button className="btn btn-secondary" onClick={() => setShowStatusEditor(true)}>
                Change
              </button>
            </div>
          </div>

          {/* Qualification Score */}
          <div className="ld-card">
            <div className="ld-card-title">Qualification Score</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <span className="qual-score-big" style={{ color: tempColor }}>{score}/4</span>
              <span className="temp-pill" style={{ background: tempColor }}>{temp}</span>
            </div>
            <div className="ld-qual-mini">
              {QUAL_ITEMS.map(item => {
                const ok = !!(lead as any)[item.key];
                return (
                  <div key={item.key} className="ld-qual-mini-row">
                    <span className={`ld-qual-dot${ok ? ' ld-qual-dot-ok' : ''}`}>{ok ? '✓' : ''}</span>
                    <span style={{ fontSize: 12, color: ok ? 'var(--teal)' : 'var(--text-muted)' }}>{item.label}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Quick Note */}
          <div className="ld-card">
            <div className="ld-card-title">Quick Note</div>
            <textarea ref={quickNoteRef} className="ld-quick-note" placeholder="Type a note and click Save…" rows={3}
              value={quickNote} onChange={e => setQuickNote(e.target.value)} />
            <button className="btn btn-primary" style={{ width: '100%', marginTop: 8 }}
              onClick={handleQuickNote} disabled={savingNote || !quickNote.trim()}>
              {savingNote ? 'Saving…' : 'Save'}
            </button>
          </div>

          {/* Assignment */}
          <div className="ld-card">
            <div className="ld-card-title">Assignment</div>
            <div className="ld-assign-field">
              <label className="ld-assign-label">Assigned To</label>
              <select className="lead-form-input"
                value={lead.assigned_to?.toString() ?? ''}
                onChange={e => patchLead({ assigned_to: e.target.value || null })}>
                <option value="">Unassigned</option>
                {agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
            <div className="ld-assign-field">
              <label className="ld-assign-label">Expected Close</label>
              <input className="lead-form-input" type="date"
                value={lead.expected_close_date ?? ''}
                onChange={e => patchLead({ expected_close_date: e.target.value || null })} />
            </div>
            <div className="ld-assign-field">
              <label className="ld-assign-label">Est. Order Value</label>
              <div className="ld-assign-val">{fmtInr(lead.expected_order_value)}</div>
            </div>
          </div>
        </div>
      </div>

      {showConvert && (
        <ConvertModal
          lead={lead}
          onClose={() => setShowConvert(false)}
          onConverted={() => { setShowConvert(false); reload(); }}
        />
      )}

      {showStatusEditor && (
        <LeadStatusEditor
          leadId={lead.id}
          currentStage={(lead.deal_stage ?? 'new') as DealStageValue}
          currentLostReason={lead.lost_reason ?? null}
          onClose={() => setShowStatusEditor(false)}
          onUpdated={reload}
        />
      )}
    </div>
  );
};

export default LeadDetail;
